import moment from "moment";
import {
  COVERED_STATUSES,
  SITES,
  formatDay,
  money,
  num,
  registerRow,
  sampleEmployees,
  versionOn,
} from "pages/PayRoll/Statutory/LWF/lwfEmployeesData";

// Payroll > Reports > Statutory Reports > Labour Welfare Fund (screens
// LWF3–LWF6a), on sample data until the LWF APIs exist. Everything is read
// from the finalised payroll register (Statutory › LWF sample), so Register
// totals = Overview = Payments payable for every site (AC-L10). Cauver does
// not pay the Board: the company pays and records the payment here.

export { formatDay, money, num, SITES };

export const PAYROLL_MONTHS = [
  { value: "2025-12", label: "Dec 2025", finalized: true, finalizedAt: "2026-01-02T11:40:00", version: "1.0" },
  { value: "2025-11", label: "Nov 2025", finalized: true, finalizedAt: "2025-12-02T10:15:00", version: "1.0" },
];
export const DEFAULT_MONTH = "2025-12";
// Validation last ran when Dec 2025 payroll was finalised.
export const CHECK_DATE = "2026-01-02T11:41:00";

export const monthLabel = (monthKey) => moment(`${monthKey}-01`).format("MMM YYYY");

const EMPLOYEES = sampleEmployees();
export const monthRows = (monthKey) => EMPLOYEES.map((employee) => registerRow(employee, monthKey)).filter(Boolean);

export const isCovered = (row) => COVERED_STATUSES.includes(row.status);

// Employee shares (collected + outstanding + paid by company) + employer.
export const totalsOf = (rows) => {
  const covered = rows.filter(isCovered);
  const employee = covered.reduce((sum, row) => sum + row.ee, 0);
  const employer = covered.reduce((sum, row) => sum + row.er, 0);
  return {
    count: covered.length,
    employee,
    employer,
    total: employee + employer,
    outstanding: covered.reduce((sum, row) => sum + row.ee_outstanding, 0),
    paidByCompany: covered.reduce((sum, row) => sum + row.ee_paid_by_company, 0),
    held: rows.filter((row) => row.status === "HELD_MISSING_DATA").length,
    notCovered: rows.filter((row) => !isCovered(row) && row.status !== "HELD_MISSING_DATA").length,
  };
};

// ---- Payment periods (B5.4): one row per site per deduction month ----

const siteVersion = (siteName, monthKey) => versionOn(SITES.find((site) => site.name === siteName), `${monthKey}-01`);

export const dueDateOf = (monthKey, dueDays) => moment(`${monthKey}-01`).endOf("month").add(Number(dueDays) || 0, "days").format("YYYY-MM-DD");

// Dec 2024 was paid before payroll moved to Cauver; kept as a payment
// period so its short payment stays visible.
const LEGACY_PERIODS = [
  { period_key: "2024-12", site: "Erode Plant", registration_no: "TN/LWF/0010293", employees: 88, ee: 1760, er: 3520, outstanding: 0, held: 0, due: "2025-01-31" },
];

export const paymentPeriods = (payments) => {
  const fromPayroll = PAYROLL_MONTHS.flatMap(({ value: monthKey }) => {
    const rows = monthRows(monthKey);
    return SITES.map((site) => {
      const version = siteVersion(site.name, monthKey);
      if (!version || version.applicability !== "COVERED" || !version.deduction_months.includes(String(moment(`${monthKey}-01`).month() + 1))) return null;
      const totals = totalsOf(rows.filter((row) => row.site_name === site.name));
      return {
        period_key: monthKey,
        site: site.name,
        registration_no: version.registration_no,
        employees: totals.count,
        ee: totals.employee,
        er: totals.employer,
        outstanding: totals.outstanding,
        held: totals.held,
        due: dueDateOf(monthKey, version.payment_due_days),
        version_name: version.name,
        state: version.state,
      };
    }).filter(Boolean);
  });
  return [...fromPayroll, ...LEGACY_PERIODS].map((period) => {
    const key = `${period.site}|${period.period_key}`;
    const list = payments.filter((payment) => payment.period === key);
    const payable = period.ee + period.er;
    const paid = list.reduce((sum, payment) => sum + payment.amount, 0);
    const status = paymentStatus(payable, paid);
    return {
      ...period,
      id: key,
      period_label: monthLabel(period.period_key),
      payable,
      paid,
      balance: payable - paid,
      payments: list,
      status,
      status_label: status === "Partly paid" ? `Partly paid · ${money(payable - paid)} short` : status,
      action: payable - paid > 0 ? "Record payment" : "View",
    };
  });
};

export const paymentStatus = (payable, paid) => (paid <= 0 ? "To pay" : paid < payable ? "Partly paid" : paid === payable ? "Paid" : "Overpaid");
export const PAYMENT_TONES = { "To pay": "amber", "Partly paid": "red", Paid: "green", Overpaid: "purple" };

export const samplePayments = () => [
  { id: 1, period: "Chennai Office|2025-12", type: "FIRST", paid_on: "2026-01-20", amount: 1800, reference: "TNLWB-2026-001201", receipt: "chennai_lwf_dec2025.pdf", remarks: "", recorded_by: "Sadheesh", recorded_at: "2026-01-20T16:05:00" },
  { id: 2, period: "Erode Plant|2025-12", type: "FIRST", paid_on: "2026-01-22", amount: 5520, reference: "TNLWB-2026-001202", receipt: "erode_plant_lwf_dec2025.pdf", remarks: "", recorded_by: "Sadheesh", recorded_at: "2026-01-22T11:30:00" },
  { id: 3, period: "Bengaluru Sales Office|2025-12", type: "FIRST", paid_on: "2026-01-12", amount: 540, reference: "KALWB-2026-77881", receipt: "", remarks: "Paid online", recorded_by: "Sadheesh", recorded_at: "2026-01-12T10:00:00" },
  { id: 4, period: "Erode Plant|2024-12", type: "FIRST", paid_on: "2025-01-28", amount: 5000, reference: "TNLWB-2025-000871", receipt: "erode_plant_lwf_dec2024.pdf", remarks: "", recorded_by: "Paramaguru", recorded_at: "2025-01-28T15:45:00" },
];

export const PAYMENT_TYPES = { FIRST: "First payment", BALANCE: "Balance payment", REVERSAL: "Reversal" };

// Period filter on Payments.
export const PERIOD_FILTERS = [
  { value: "12", label: "Last 12 months" },
  { value: "24", label: "Last 2 years" },
  { value: "all", label: "All" },
];
export const inPeriodFilter = (period, filter, asOf) => {
  if (filter === "all") return true;
  return moment(`${period.period_key}-01`).isAfter(moment(asOf).subtract(Number(filter), "months"));
};

// ---- Validation (B7.4) ----

export const SEVERITY_COLORS = { Blocking: "#DC2626", Warning: "#F0954A", Info: "#2563EB" };

// asOf: when the check ran (overdue payments are judged on that date).
export const buildIssues = (monthKey, periods, asOf) => {
  const rows = monthRows(monthKey);
  const label = monthLabel(monthKey);
  const issues = [];
  const notSetUp = {};
  rows.filter((row) => row.status === "SITE_NOT_SET_UP").forEach((row) => {
    notSetUp[row.site_name] = (notSetUp[row.site_name] || 0) + 1;
  });
  Object.entries(notSetUp).forEach(([site, count]) =>
    issues.push({
      id: `site-${site}`,
      rule: "SITE_NOT_SET_UP",
      kind: "site",
      name: `${site} · ${count} employees`,
      title: site,
      emp_id: "—",
      site,
      issue: "Site has staff but no LWF set-up (or not marked “Not applicable”)",
      severity: "Blocking",
      impact: "LWF not worked out · payroll can still finalise",
      fix_in: "LWF Configuration",
      problem: "No LWF set-up for this site",
      effect: `No LWF for ${count} employees in ${label}`,
      effect_note: "Your company may still owe employee + employer LWF to the Board",
      steps: [
        ["Set up the site", `Configuration › Labour Welfare Fund › ${site} — or Copy from site`],
        ["Or mark Not applicable", "If the state has no LWF for this site"],
        ["Re-check", "Re-check now, or it runs by itself on save"],
      ],
      meta: `${SITES.find((item) => item.name === site)?.state || ""} · ${count} employees work here`,
    }),
  );
  rows.forEach((row) => {
    const person = { emp_id: row.emp_id, site: row.site_name, name: row.name, title: row.name, kind: "employee", meta: [row.emp_id, row.department, row.site_name].join(" · ") };
    if (row.status === "HELD_MISSING_DATA") {
      issues.push({
        ...person,
        id: `held-${row.id}`,
        rule: "CATEGORY_MISSING",
        issue: "Employee category missing — this site excludes some categories",
        severity: "Blocking",
        impact: "LWF held · payroll can still finalise",
        fix_in: "Employee profile",
        problem: "Employee category missing",
        effect: `LWF held for ${label} — not worked out and never shown as ₹0`,
        effect_note: `${row.site_name} does not cover: ${(row.version?.excluded_categories || []).join(", ")}`,
        steps: [
          ["Set the category", "Employee profile › Category"],
          ["Work out held LWF", "Then the same rules and site version as the deduction month are applied; the amount is added to that month's payable"],
        ],
      });
    } else if (row.status === "EE_OUTSTANDING") {
      issues.push({
        ...person,
        id: `out-${row.id}`,
        rule: "LOW_PAY",
        issue: `On the rolls on ${row.rolls?.label.replace(/^On the rolls on | \(set date\)$/g, "") || label} but no pay — employee share ${money(row.ee)} recorded as outstanding`,
        severity: "Warning",
        impact: `${money(row.ee)} to recover or pay`,
        fix_in: "Payroll / HR",
        problem: "Pay too low to deduct the employee share",
        effect: `Employee share ${money(row.ee)} outstanding — still part of the payable to the Board`,
        effect_note: "Employer share is due as usual",
        steps: [["Recover or pay", "Recover the employee share outside LWF, or let the company pay it (automatic recovery comes in V2)"]],
      });
    } else if (row.status === "NOT_COVERED_CATEGORY" || row.status === "NOT_COVERED_WAGE") {
      issues.push({
        ...person,
        id: `nc-${row.id}`,
        rule: "NOT_COVERED",
        issue: row.status === "NOT_COVERED_CATEGORY" ? `Not covered — category ${row.category} is excluded at this site` : "Not covered — LWF wage is above the site's wage limit",
        severity: "Info",
        impact: "None",
        fix_in: "—",
        problem: row.status === "NOT_COVERED_CATEGORY" ? `Category ${row.category} is not covered at ${row.site_name}` : "Above the wage limit",
        effect: "No LWF for this employee",
        steps: [],
      });
    }
  });
  periods
    .filter((period) => period.balance > 0 && moment(period.due).isBefore(moment(asOf), "day"))
    .forEach((period) =>
      issues.push({
        id: `pay-${period.id}`,
        rule: "PAYMENT_OVERDUE",
        kind: "payment",
        name: `${period.site} · ${period.period_label}`,
        title: `${period.site} · ${period.period_label}`,
        emp_id: "—",
        site: period.site,
        period_id: period.id,
        issue:
          period.status === "Partly paid"
            ? `Payment ${money(period.balance)} short — due date ${formatDay(period.due)} passed`
            : `Payment ${money(period.balance)} not recorded — due date ${formatDay(period.due)} passed`,
        severity: "Warning",
        impact: "Payment only",
        fix_in: "Payments",
        problem: period.status === "Partly paid" ? `${money(period.balance)} short` : "No payment recorded",
        effect: `Board balance ${money(period.balance)} for ${period.period_label}`,
        steps: [["Pay the Board", "Pay the balance to the state Labour Welfare Board"], ["Record the payment", "Payments › Record payment (Balance payment)"]],
        meta: `${period.registration_no} · payable ${money(period.payable)}`,
      }),
    );
  const order = { Blocking: 0, Warning: 1, Info: 2 };
  return issues.sort((a, b) => order[a.severity] - order[b.severity]);
};
